import authService from './authService';
import { frontendLogger } from '../../services/frontendLogger.js';

/**
 * Tour Service - Frontend service for tour management
 * Handles communication with backend tour API
 */

const BASE_URL = '/tour';

class TourService {
  // Get user's current tour status
  async getTourStatus() {
    try {
      const response = await authService.request(`${BASE_URL}/status`);
      return response;
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Start tour for user
  async startTour(startStep = 0) {
    try {
      const response = await authService.request(`${BASE_URL}/start`, {
        method: 'POST',
        body: JSON.stringify({ startStep })
      });
      return response;
    } catch (error) {
      frontendLogger.error('Failed to start tour', {
        error: error.message || 'Unknown error',
        context: {
          service: 'TourService',
          operation: 'startTour',
          startStep,
          endpoint: `${BASE_URL}/start`
        }
      });
      return { success: false, error: error.message };
    }
  }

  // Complete tour
  async completeTour(completionData) {
    try {
      const response = await authService.request(`${BASE_URL}/complete`, {
        method: 'POST',
        body: JSON.stringify(completionData)
      });
      return response;
    } catch (error) {
      frontendLogger.error('Failed to complete tour', {
        error: error.message || 'Unknown error',
        context: {
          service: 'TourService',
          operation: 'completeTour',
          completionData: Object.keys(completionData || {}),
          endpoint: `${BASE_URL}/complete`
        }
      });
      return { success: false, error: error.message };
    }
  }

  // Skip tour
  async skipTour(skipData) {
    try {
      const response = await authService.request(`${BASE_URL}/skip`, {
        method: 'POST',
        body: JSON.stringify(skipData)
      });
      return response;
    } catch (error) {
      frontendLogger.error('Failed to skip tour', {
        error: error.message || 'Unknown error',
        context: {
          service: 'TourService',
          operation: 'skipTour',
          skipData: Object.keys(skipData || {}),
          endpoint: `${BASE_URL}/skip`
        }
      });
      return { success: false, error: error.message };
    }
  }

  // Restart tour
  async restartTour() {
    try {
      const response = await authService.request(`${BASE_URL}/restart`, {
        method: 'POST'
      });
      return response;
    } catch (error) {
      frontendLogger.error('Failed to restart tour', {
        error: error.message || 'Unknown error',
        context: {
          service: 'TourService',
          operation: 'restartTour',
          endpoint: `${BASE_URL}/restart`
        }
      });
      return { success: false, error: error.message };
    }
  }

  // Update tour preferences (e.g., never show again)
  async updateTourPreferences(preferences) {
    try {
      const response = await authService.request(`${BASE_URL}/preferences`, {
        method: 'PUT',
        body: JSON.stringify(preferences)
      });
      return response;
    } catch (error) {
      frontendLogger.error('Failed to update tour preferences', {
        error: error.message || 'Unknown error',
        context: {
          service: 'TourService',
          operation: 'updateTourPreferences',
          preferences: Object.keys(preferences || {}),
          endpoint: `${BASE_URL}/preferences`
        }
      });
      return { success: false, error: error.message };
    }
  }

  // Reset tour preferences (remove "never show again" setting)
  async resetTourPreferences() {
    try {
      // Clear localStorage
      localStorage.removeItem('tour_never_show_again');
      
      // Update backend to remove neverShowTourAgain flag
      const response = await this.updateTourPreferences({
        neverShowTourAgain: false,
        resetAt: new Date().toISOString()
      });
      
      return response;
    } catch (error) {
      frontendLogger.error('Failed to reset tour preferences', {
        error: error.message || 'Unknown error',
        context: {
          service: 'TourService',
          operation: 'resetTourPreferences',
          endpoint: `${BASE_URL}/preferences`
        }
      });
      return { success: false, error: error.message };
    }
  }

  // Get tour analytics (admin only)
  async getTourAnalytics(filters = {}) {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const url = `${BASE_URL}/analytics${queryParams ? `?${queryParams}` : ''}`;
      
      const response = await authService.request(url);
      return response;
    } catch (error) {
      frontendLogger.error('Failed to get tour analytics', {
        error: error.message || 'Unknown error',
        context: {
          service: 'TourService',
          operation: 'getTourAnalytics',
          filters: Object.keys(filters || {}),
          endpoint: `${BASE_URL}/analytics`
        }
      });
      return { success: false, error: error.message };
    }
  }

  // Local storage helpers for tour state
  saveTourState(tourState) {
    try {
      localStorage.setItem('sanctuary_tour_state', JSON.stringify({
        ...tourState,
        savedAt: new Date().toISOString()
      }));
    } catch (error) {
      frontendLogger.error('Failed to save tour state to localStorage', {
        error: error.message || 'Unknown error',
        context: {
          service: 'TourService',
          operation: 'saveTourStateToLocalStorage',
          stateKeys: Object.keys(tourState || {}),
          localStorageKey: 'sanctuary_tour_state'
        }
      });
    }
  }

  getTourState() {
    try {
      const saved = localStorage.getItem('sanctuary_tour_state');
      if (saved) {
        const state = JSON.parse(saved);
        // Return state if it's less than 24 hours old
        const savedAt = new Date(state.savedAt);
        const now = new Date();
        const hoursDiff = (now - savedAt) / (1000 * 60 * 60);
        
        if (hoursDiff < 24) {
          return state;
        }
      }
      return null;
    } catch (error) {
      frontendLogger.error('Failed to get tour state from localStorage', {
        error: error.message || 'Unknown error',
        context: {
          service: 'TourService',
          operation: 'getTourStateFromLocalStorage',
          localStorageKey: 'sanctuary_tour_state'
        }
      });
      return null;
    }
  }

  clearTourState() {
    try {
      localStorage.removeItem('sanctuary_tour_state');
    } catch (error) {
      frontendLogger.error('Failed to clear tour state from localStorage', {
        error: error.message || 'Unknown error',
        context: {
          service: 'TourService',
          operation: 'clearTourStateFromLocalStorage',
          localStorageKey: 'sanctuary_tour_state'
        }
      });
    }
  }

  // Check if user should see tour
  async shouldShowTour(userProfile) {
    try {
      // First check localStorage for immediate response
      const localNeverShow = localStorage.getItem('tour_never_show_again');
      if (localNeverShow === 'true') {
        return false;
      }

      // Get tour status from backend
      const result = await this.getTourStatus();
      
      if (!result.success) {
        // If can't get status, check local state
        const localState = this.getTourState();
        return !localState?.completed;
      }

      const { tourStatus } = result;

      // Check if user chose "never show again" in backend preferences
      if (tourStatus.preferences?.neverShowTourAgain) {
        // Sync backend preference to localStorage for faster future checks
        localStorage.setItem('tour_never_show_again', 'true');
        return false;
      }
      
      // Show tour if:
      // 1. Never completed tour
      // 2. User explicitly restarted tour  
      // 3. New user (created within last hour)
      const isNewUser = userProfile?.created_at && 
        (new Date() - new Date(userProfile.created_at)) < (60 * 60 * 1000);
      
      return !tourStatus.completed || isNewUser;
    } catch (error) {
      frontendLogger.error('Failed to check if should show tour', {
        error: error.message || 'Unknown error',
        context: {
          service: 'TourService',
          operation: 'shouldShowTour',
          hasProfile: !!userProfile,
          userTier: userProfile?.tier,
          tourCompleted: userProfile?.tour_completed,
          userCreatedAt: userProfile?.created_at,
          localNeverShow: localStorage.getItem('tour_never_show_again')
        }
      });
      // Default to showing tour for new users (unless explicitly hidden locally)
      return localStorage.getItem('tour_never_show_again') !== 'true';
    }
  }

  // Estimate time remaining
  estimateTimeRemaining(currentStep, totalSteps, avgTimePerStep = 30) {
    const remainingSteps = totalSteps - currentStep;
    const remainingSeconds = remainingSteps * avgTimePerStep;
    
    if (remainingSeconds < 60) {
      return `${remainingSeconds}s`;
    } else if (remainingSeconds < 3600) {
      const minutes = Math.ceil(remainingSeconds / 60);
      return `${minutes}m`;
    } else {
      const hours = Math.ceil(remainingSeconds / 3600);
      return `${hours}h`;
    }
  }
}

export default new TourService();